import React, { useState, useEffect } from 'react';
import { Icon, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { NavLinkAdapter, FuseUtils } from '@fuse';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from 'app/store/actions';
import NavBadge from './FuseNavBadge';
import { isTrainingEnabled } from '@config';

const useStyles = makeStyles(theme => ({
	item: {
		height: 40,
		paddingRight: 12,
		'&.active': {
			backgroundColor: theme.palette.secondary.main,
			color: theme.palette.secondary.contrastText + '!important',
			pointerEvents: 'none',
			transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
			'& .list-item-text-primary': {
				color: 'inherit'
			},
			'& .list-item-icon': {
				color: 'inherit'
			}
		},
		'&.square, &.active.square': {
			width: '100%',
			borderRadius: '0'
		},
		'& .list-item-icon': {},
		'& .list-item-text': {},
		color: theme.palette.text.primary,
		cursor: 'pointer',
		textDecoration: 'none!important'
	},
	disabledItem: {
		opacity: 0.4,
		cursor: 'not-allowed',
		pointerEvents: 'none',
		'& .list-item-icon': {
			color: theme.palette.text.disabled
		},
		'& .list-item-text-primary': {
			color: theme.palette.text.disabled
		}
	}
}));

const NavItem = props => {
	const dispatch = useDispatch();
	const userRole = useSelector(({ auth }) => auth.user.role);
	const user = useSelector(({ auth }) => auth.user);

	const [showTraining, setShowTraining] = useState(false);

	const classes = useStyles(props);
	const { item, nestedLevel, active } = props;
	let paddingValue = 40 + nestedLevel * 16;
	const listItemPadding = nestedLevel > 0 ? 'pl-' + (paddingValue > 80 ? 80 : paddingValue) : 'pl-24';

	useEffect(() => {
		const getTraining = async () => {
			const training = await isTrainingEnabled();
			setShowTraining(training);
		}
		if (item.title === "Trainings") getTraining();
	}, [item]);

	if (item.title === "Trainings" && !showTraining) return null;

	// Check authorization for all users
	if (!FuseUtils.hasPermission(item.auth, userRole)) {
		return null;
	}

	// For participants, also check training_allow flag
	if (user.participant && !item.training_allow) {
		return null;
	}

	// Check if item should be disabled for current user
	const isDisabled = item.disabled && FuseUtils.hasPermission(item.disabled, userRole);

	return (
		<ListItem
			button
			component={isDisabled ? 'div' : NavLinkAdapter}
			to={isDisabled ? undefined : item.url}
			activeClassName={isDisabled ? undefined : "active"}
			className={clsx(
				classes.item,
				listItemPadding,
				'list-item',
				active,
				isDisabled && classes.disabledItem
			)}
			onClick={isDisabled ? undefined : () => dispatch(Actions.navbarCloseMobile())}
			exact={item.exact}
			disabled={isDisabled}
		>
			{item.icon && (
				<Icon className="list-item-icon text-16 flex-shrink-0 mr-16" color="action">
					{item.icon}
				</Icon>
			)}
			<ListItemText
				className="list-item-text"
				primary={item.title}
				classes={{ primary: 'text-14 list-item-text-primary' }}
			/>
			{item.badge && <NavBadge badge={item.badge} />}
		</ListItem>
	);
};

NavItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		icon: PropTypes.string,
		url: PropTypes.string
	})
};

NavItem.defaultProps = {};

export default withRouter(React.memo(NavItem));
